import type { EventRow } from './types';
import { dartRound } from './types';

/**
 * 창 · 기간 필터 — `lens_select_recent_runs` · `lens_filter_by_range` 의 TS 짝.
 *
 * ⚠️⚠️ **둘 다 회차 단위로 자른다. 이벤트 단위가 아니다.**
 * 이벤트로 자르면 창 경계가 회차 한가운데를 지나가, 그 회차의 `run_started` 는
 * 창 밖인데 `ending_reached` 는 안이 되어 **분모에서만 빠지고 완주율이 부푼다.**
 * ★ 실측(2026-09-21): Firestore 21회차에서 기간 7일 완주율 **100.0%**,
 *   전체 **28.6%**. 100% 가 「모두 완주했다」로 읽힌다 — 거짓말이다.
 */

/**
 * 최근 N개 「쓸 만한」 회차의 이벤트만 남긴다.
 *
 * ⚠️ 커스텀 액션 `lensFetchRecentRunEvents`(서버 쿼리)와 **같은 판별식**이다 —
 * 한쪽만 고치면 목업과 Firestore 가 다른 표본을 보게 되고, 그러면 두 화면을
 * 비교해 버그를 좁히던 방법이 없어진다.
 */
export function lensSelectRecentRuns(
  events?: EventRow[] | null,
  runLimit?: number | null,
  minTurns?: number | null,
): EventRow[] {
  const rows = events ?? [];
  if (rows.length === 0) return rows;
  const wanted = dartRound(runLimit ?? 100);
  const minT = dartRound(minTurns ?? 2);

  const startedAt = new Map<string, Date>();
  const turns = new Map<string, number>();
  const hasModel = new Set<string>();
  for (const e of rows) {
    const id = e.runId;
    if (!id) continue;
    if (e.eventName === 'run_started' && e.occurredAt !== null) {
      startedAt.set(id, e.occurredAt);
    }
    const n = e.eventName;
    if (
      n === 'action_interpreted' ||
      n === 'unsupported_action_requested' ||
      n === 'input_blocked'
    ) {
      turns.set(id, (turns.get(id) ?? 0) + 1);
    }
    if (e.modelVersion !== '') hasModel.add(id);
  }

  // ⚠️ 쓰레기 회차 필터가 **창을 세는 조건 안에** 있다. 나중에 붙이면 창
  //    크기를 다시 잡아야 한다(CLAUDE.md).
  const good: string[] = [];
  for (const id of startedAt.keys()) {
    if ((turns.get(id) ?? 0) < minT) continue;
    if (!hasModel.has(id)) continue;
    good.push(id);
  }
  // 최신순으로 창을 채운다.
  good.sort((a, b) => startedAt.get(b)!.getTime() - startedAt.get(a)!.getTime());
  const keep = new Set(good.length <= wanted ? good : good.slice(0, wanted));
  return rows.filter((e) => keep.has(e.runId));
}

/**
 * `dateRangeKey` 로 기간 필터.
 *
 * ★ 키는 `all` · `24h` · `7d` · `30d` 다(`dsl/edit.dart` `_rangeChip`).
 * ⚠️ **모르는 키는 조용히 30일로 떨어진다.** 오류가 아니다 — 원본이 그렇고,
 *    이식 1판의 목표는 숫자가 같은 것이므로 그대로 둔다.
 *
 * ⚠️ **기준은 현재 시각이다.** 데이터의 마지막 시각으로 자르면 수집이 3일 전에
 * 멈춰도 「오늘」에 데이터가 보여서 장애를 못 알아챈다. 비면 비는 게 맞는 정보다.
 */
export function lensFilterByRange(
  events?: EventRow[] | null,
  rangeKey?: string | null,
  now = new Date(),
): EventRow[] {
  const rows = events ?? [];
  const key = rangeKey ?? 'all';
  if (key === 'all' || rows.length === 0) return rows;

  const days = key === '24h' ? 1 : key === '7d' ? 7 : 30;
  const cutoff = now.getTime() - days * 86_400_000;

  // → 회차의 이벤트가 **하나라도** 창 안이면 그 회차를 통째로 남긴다.
  const keep = new Set<string>();
  for (const e of rows) {
    const at = e.occurredAt;
    if (at === null) continue;
    if (at.getTime() >= cutoff) keep.add(e.runId);
  }

  const out: EventRow[] = [];
  for (const e of rows) {
    // ⚠️ `run_id` 가 빈 이벤트는 회차에 못 묶는다 — 시각으로만 거른다.
    if (e.runId === '') {
      const at = e.occurredAt;
      if (at !== null && at.getTime() >= cutoff) out.push(e);
      continue;
    }
    if (keep.has(e.runId)) out.push(e);
  }
  return out;
}
